<template>
  <section
    class="doctor-policy campaign-area"
    :dir="uiLocale === 'en' ? 'ltr' : 'rtl'"
    :style="{ textAlign: uiLocale === 'en' ? 'left' : 'right' }"
  >
    <header><h1>{{ words.title }}</h1><p>{{ words.intro }}</p></header>
    <label>{{ words.language }}<select
      :value="locale"
      :disabled="loading || saving || dirty"
      @change="switchLocale($event.target.value)"
    ><option value="fa">فارسی</option><option value="en">English</option><option value="ar">العربية</option></select></label>
    <p
      v-if="dirty"
      role="status"
    >
      {{ words.unsaved }}
    </p>
    <p
      v-if="loading"
      role="status"
    >
      {{ words.loading }}
    </p>
    <p
      v-if="message"
      role="status"
    >
      {{ message }}
    </p>
    <template v-if="loaded">
      <p v-if="!canWrite">
        {{ words.noWrite }}
      </p>
      <fieldset :disabled="!canWrite || saving">
        <div class="campaign-panel">
          <label class="check"><input
            v-model="policy.enabled"
            type="checkbox"
          >{{ words.enabled }}</label><p>{{ words.enabledHint }}</p>
          <div class="campaign-grid">
            <label>{{ words.count }}<select v-model.number="policy.maxDoctors"><option
              v-for="n in 3"
              :key="n"
              :value="n"
            >{{ n }}</option></select></label><label>{{ words.placement }}<select v-model="policy.placement"><option value="middle">{{ words.middle }}</option><option value="end">{{ words.end }}</option></select></label>
          </div>
        </div>
        <div class="campaign-panel doctor-copy-editor">
          <h2>{{ words.copyTitle }}</h2><p id="doctor-copy-hint">
            {{ words.copyHint }}
          </p>
          <label>{{ words.copyEyebrow }}<input
            v-model="policy.blockCopy.eyebrow"
            :dir="locale === 'en' ? 'ltr' : 'rtl'"
            :style="{ textAlign: locale === 'en' ? 'left' : 'right' }"
            maxlength="100"
            aria-describedby="doctor-copy-hint"
          ></label>
          <label>{{ words.copyHeading }}<input
            v-model="policy.blockCopy.title"
            :dir="locale === 'en' ? 'ltr' : 'rtl'"
            :style="{ textAlign: locale === 'en' ? 'left' : 'right' }"
            maxlength="160"
            aria-describedby="doctor-copy-hint"
          ></label>
          <label>{{ words.copyIntro }}<input
            v-model="policy.blockCopy.intro"
            :dir="locale === 'en' ? 'ltr' : 'rtl'"
            :style="{ textAlign: locale === 'en' ? 'left' : 'right' }"
            maxlength="500"
            aria-describedby="doctor-copy-hint"
          ></label>
        </div>
        <ArticleDoctorEditor
          :ids="policy.defaultDoctorIds"
          mode="manual"
          selection-only
          orderable
          :selection-title="words.defaults"
          :selection-intro="words.defaultsHint"
          :lang="locale"
          @change="setDefaults($event)"
        />
        <h2>{{ words.rules }}</h2><p>{{ words.rulesHint }}</p>
        <p v-if="!policy.rules.length">
          {{ words.empty }}
        </p>
        <article
          v-for="(rule, index) in policy.rules"
          :key="rule.id"
          class="campaign-panel"
        >
          <div class="campaign-actions">
            <strong>{{ index + 1 }}</strong><label class="check"><input
              v-model="rule.enabled"
              type="checkbox"
            >{{ words.active }}</label><button
              type="button"
              :disabled="index === 0"
              :aria-label="`${words.up}: ${rule.name}`"
              @click="move(index, -1)"
            >
              {{ words.up }}
            </button><button
              type="button"
              :disabled="index === policy.rules.length - 1"
              :aria-label="`${words.down}: ${rule.name}`"
              @click="move(index, 1)"
            >
              {{ words.down }}
            </button><button
              type="button"
              :aria-label="`${words.remove}: ${rule.name}`"
              @click="remove(index)"
            >
              {{ words.remove }}
            </button>
          </div>
          <label>{{ words.name }}<input
            v-model="rule.name"
            maxlength="100"
          ></label>
          <h3>{{ words.targets }}</h3><p>{{ words.matching }}</p>
          <DoctorTargetPicker
            v-model="rule.tagIds"
            kind="tag"
            :title="words.tags"
            :locale="locale"
          />
          <details :open="rule.articleIds.length > 0 || rule.categoryIds.length > 0 || rule.keywords.length > 0">
            <summary>{{ words.more }}</summary><div class="campaign-grid">
              <DoctorTargetPicker
                v-model="rule.articleIds"
                kind="article"
                :title="words.articles"
                :locale="locale"
              /><DoctorTargetPicker
                v-model="rule.categoryIds"
                kind="category"
                :title="words.categories"
                :locale="locale"
              />
            </div><label>{{ words.keywords }}<textarea
              :value="rule.keywords.join('\n')"
              rows="3"
              maxlength="809"
              @change="setKeywords(rule, $event.target.value)"
            /></label><small>{{ words.keywordHint }}</small>
          </details>
          <ArticleDoctorEditor
            :ids="rule.doctorIds"
            mode="manual"
            selection-only
            :lang="locale"
            @change="setDoctors(rule, $event)"
          />
        </article>
        <button
          type="button"
          :disabled="policy.rules.length >= 30"
          @click="add"
        >
          {{ words.add }}
        </button>
        <div class="doctor-policy-save">
          <button
            type="button"
            :disabled="saving || !dirty"
            class="primary"
            @click="save"
          >
            {{ saving ? words.saving : words.save }}
          </button><button
            type="button"
            :disabled="saving || !dirty"
            @click="discard"
          >
            {{ words.discard }}
          </button>
        </div>
      </fieldset>
      <section class="campaign-panel">
        <h2>{{ words.preview }}</h2><p>{{ words.previewHint }}</p><DoctorTargetPicker
          v-model="previewIds"
          kind="article"
          :title="words.articles"
          :maximum="1"
          :locale="locale"
        /><button
          type="button"
          :disabled="previewLoading || !previewIds.length || dirty || saving"
          @click="preview"
        >
          {{ previewLoading ? words.loading : words.previewButton }}
        </button><p
          v-if="previewMessage"
          role="status"
        >
          {{ previewMessage }}
        </p><div v-if="result">
          <p>{{ resultLabel }}</p><p v-if="!result.enabled || !result.doctors.length">
            {{ words.none }}
          </p><ul
            v-else
            class="doctor-preview"
          >
            <li
              v-for="doctor in result.doctors"
              :key="doctor.id"
            >
              <strong><bdi dir="auto">{{ doctor.name }}</bdi></strong><span><bdi dir="auto">{{ doctor.speciality }}</bdi></span>
            </li>
          </ul>
        </div>
      </section>
    </template>
  </section>
</template>
<script>
import axios from '@/libs/axios'
import ArticleDoctorEditor from '../article/components/ArticleDoctorEditor.vue'
import DoctorTargetPicker from './DoctorTargetPicker.vue'
import messages from './messages'
import {
  emptyBlockCopy, newRule, policyPayload, validPolicy,
} from './model'
import '../contentCampaign/campaign.css'

export default {
  components: { ArticleDoctorEditor, DoctorTargetPicker },
  data() {
    return {
      locale: 'fa',
      policy: {
        version: 0, blockCopy: emptyBlockCopy(), enabled: true, maxDoctors: 3, defaultDoctorIds: [], placement: 'middle', rules: [],
      },
      baseline: '',
      loaded: false,
      loading: false,
      saving: false,
      message: '',
      sequence: 0,
      ruleSequence: 0,
      previewIds: [],
      previewLoading: false,
      previewMessage: '',
      previewSequence: 0,
      result: null,
    }
  },
  computed: {
    uiLocale() { return this.$i18n.locale },
    words() { return messages[this.uiLocale] || messages.fa },
    canWrite() { return this.$can('update', 'article') },
    dirty() { return this.loaded && JSON.stringify(this.policy) !== this.baseline },
    resultLabel() {
      if (!this.result) return ''; if (this.result.source === 'rule') return `${this.words.rule}: ${this.result.ruleName || ''}`; return this.words[{
        tags: 'automatic', default: 'defaultSource', general: 'generalSource', manual: 'manual', disabled: 'disabled', none: 'none',
      }[this.result.source]] || ''
    },
  },
  watch: { dirty(value) { if (value) { this.previewSequence += 1; this.previewLoading = false; this.result = null; this.previewMessage = '' } }, previewIds() { this.previewSequence += 1; this.previewLoading = false; this.result = null; this.previewMessage = '' } },
  created() { this.load() },
  beforeDestroy() { this.sequence += 1; this.previewSequence += 1 },
  methods: {
    accept(data) {
      if (!data || !validPolicy(data)) throw new Error('INVALID_DOCTOR_POLICY'); this.policy = {
        ...data, blockCopy: data.blockCopy === undefined ? emptyBlockCopy() : { ...data.blockCopy }, defaultDoctorIds: data.defaultDoctorIds === undefined ? [] : [...data.defaultDoctorIds], rules: [...data.rules].sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id)),
      }; this.baseline = JSON.stringify(this.policy); this.loaded = true
    },
    async load() {
      this.sequence += 1; const { sequence } = this; this.loading = true; this.loaded = false; this.message = ''
      try { const response = await axios.get('articles/doctor-policy', { headers: { lang: this.locale }, timeout: 10000 }); if (sequence === this.sequence) this.accept(response.data) } catch (error) { if (sequence === this.sequence) this.message = this.words.failed } finally { if (sequence === this.sequence) this.loading = false }
    },
    switchLocale(locale) { if (this.dirty || this.saving) return; this.locale = locale; this.previewSequence += 1; this.previewIds = []; this.result = null; this.load() },
    discard() { if (this.saving || !this.baseline) return; this.policy = JSON.parse(this.baseline); this.message = ''; this.result = null },
    add() { if (!this.canWrite || this.policy.rules.length >= 30) return; this.ruleSequence += 1; this.policy.rules.push(newRule(`rule-${Date.now()}-${this.ruleSequence}`)) },
    remove(index) { if (this.canWrite) this.policy.rules.splice(index, 1) },
    move(index, direction) { const next = index + direction; if (!this.canWrite || next < 0 || next >= this.policy.rules.length) return; const rules = [...this.policy.rules]; [rules[index], rules[next]] = [rules[next], rules[index]]; this.policy.rules = rules },
    setKeywords(rule, text) { this.$set(rule, 'keywords', text.split('\n').map(value => value.trim()).filter(Boolean)) },
    setDefaults(patch) { if (patch.recommendedDoctorIds) this.policy.defaultDoctorIds = [...patch.recommendedDoctorIds] },
    setDoctors(rule, patch) { if (patch.recommendedDoctorIds) this.$set(rule, 'doctorIds', patch.recommendedDoctorIds) },
    async save() {
      if (!this.canWrite || this.saving || !this.loaded) return
      const payload = policyPayload(this.policy); if (!validPolicy(payload)) { this.message = this.words.invalid; return }
      this.saving = true; this.message = ''; this.result = null; this.previewSequence += 1
      try { const response = await axios.put('articles/doctor-policy', payload, { headers: { lang: this.locale }, timeout: 10000 }); this.accept(response.data); this.message = this.words.saved } catch (error) { this.message = error.response && error.response.status === 409 ? this.words.conflict : this.words.failed } finally { this.saving = false }
    },
    async preview() {
      if (!this.previewIds.length || this.previewLoading || this.dirty) return
      this.previewSequence += 1; const sequence = this.previewSequence; this.previewLoading = true; this.previewMessage = ''; this.result = null
      try { const response = await axios.post('articles/doctor-preview', { articleId: this.previewIds[0] }, { headers: { lang: this.locale }, timeout: 10000 }); if (sequence !== this.previewSequence) return; if (!response.data || !Array.isArray(response.data.doctors) || typeof response.data.enabled !== 'boolean') throw new Error('INVALID_PREVIEW'); this.result = response.data } catch (error) { if (sequence === this.previewSequence) this.previewMessage = this.words.failed } finally { if (sequence === this.previewSequence) this.previewLoading = false }
    },
  },
}
</script>
<style scoped>
.doctor-policy { max-width: 1100px; margin: auto; }
.doctor-policy h1 { font-size: 24px; } .doctor-policy h2 { font-size: 19px; } .doctor-policy h3 { font-size: 16px; }
.doctor-policy fieldset { border: 0; padding: 0; min-width: 0; }
.doctor-policy details { margin-top: 18px; } .doctor-policy summary { padding: 12px 0; cursor: pointer; }
.doctor-policy-save { display: flex; flex-wrap: wrap; gap: 12px; margin: 22px 0; } .doctor-preview { list-style: none; padding: 0; display: grid; grid-template-columns: repeat(auto-fit,minmax(180px,1fr)); gap: 12px; }
.doctor-preview li { background: #edf7f6; border-radius: 12px; padding: 16px; color: #345b5a; } .doctor-preview span { display: block; margin-top: 8px; }
</style>
